import React, { useState, useEffect, useCallback } from 'react';
import {
  pspLogin, pspLogout, pspMe, pspChangePassword, pspForgotPassword, pspResetPassword,
  getPSPLineItems, updatePSPOrderStatus, updatePSPItemStatus, bulkUpdatePSPItemStatus, qcDecisionPSP,
  getPSPItemFiles, uploadModeledFile, getPSPItemPartnerData, getPSPItemModels,
  adminUserMe, getPSPCompanies, setPSPAdminView, pspDownloadUrl, // Phase 12 — admin view
  default as api,
  createPackingSlip, getPSPPackingSlips, discardPSPPackingSlip,
} from '../api';
import PartnerData from '../components/PartnerData'; // Phase 12
import PSPModels from '../components/PSPModels';     // Phase 12b

const STAGE_LABEL = {
  generating: 'Generating RAW', raw_generated: 'RAW generated', raw_accepted: 'RAW accepted',
  cleaning: 'Cleaning', quality_approved: 'Quality approved', awaiting_payment: 'Awaiting payment',
};

// Phase 12 — admin view: admins see every PSP's assigned items.
// { isAdmin, pspFilter } — pspFilter is '' (all PSPs) or a PSP company id.
const PSPAdminContext = React.createContext({ isAdmin: false, pspFilter: '' });
import { printPackingSlip } from '../utils/packingSlip';
import { Badge, RushBadge, StatusMultiSelect, LazyImage, Spinner, useToast, Toasts } from '../components/UI';

// Phase 9 — 8-stage PSP workflow: RTM → SCP → RTP → PRT → FIN → QCI → PKG → STS
const PSP_ITEM_STATUSES = ['ready_to_model','sculpting','ready_to_print','printing','finishing','qc','packing','shipped_to_solidiphy'];
const STATUS_LABELS = {
  ready_to_model:       'Ready to Model',
  sculpting:            'Sculpting',
  ready_to_print:       'Ready to Print',
  printing:             'Printing',
  finishing:            'Finishing',
  qc:                   'QC',
  packing:              'Packing',
  shipped_to_solidiphy: 'Shipped to Solidiphy',
};
// Free movement in any direction among the 7 editable statuses (mirrors backend PSP_TRANSITIONS_ANY).
// QC has no dropdown transitions — handled by dedicated Pass/Fail buttons instead.
const PSP_EDITABLE_STATUSES = ['ready_to_model','sculpting','ready_to_print','printing','finishing','qc','packing'];
const PSP_ITEM_TRANSITIONS = Object.fromEntries(
  PSP_EDITABLE_STATUSES.map(s => [s, s === 'qc' ? [] : PSP_EDITABLE_STATUSES.filter(x => x !== s)])
);
PSP_ITEM_TRANSITIONS.shipped_to_solidiphy = [];
// QC fail reason options (mirrors backend QC_FAIL_TARGET)
const QC_FAIL_REASONS = ['Print Issue', 'Post Processing Issue', 'Digital Issue'];
// Mirrors backend QC_FAIL_TARGET — used for optimistic UI updates only; the
// server is still the source of truth and recomputes this independently.
const QC_FAIL_TARGET = {
  'Print Issue':           'printing',
  'Post Processing Issue': 'printing',
  'Digital Issue':         'sculpting',
};
const API_BASE = process.env.REACT_APP_API_URL || '';

// Background shade by depth: Company (darkest) → Group → Order → Line item (lightest)
const DEPTH_BG = [
  'var(--color-background-quaternary)',
  'var(--color-background-tertiary)',
  'var(--color-background-secondary)',
  'var(--color-background-primary)',
];
const depthBg = (depth) => DEPTH_BG[Math.min(depth, DEPTH_BG.length - 1)];

const GROUP_OPTIONS = [
  { id:'company', label:'Company' },
  { id:'group',   label:'Group'   },
  { id:'sku',     label:'SKU'     },
  { id:'order',   label:'Order'   },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}

function validatePwList(pw) {
  const errs = [];
  if (!pw || pw.length < 8)              errs.push('At least 8 characters');
  if (!/[A-Z]/.test(pw))                 errs.push('At least one uppercase letter');
  if (!/[a-z]/.test(pw))                 errs.push('At least one lowercase letter');
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw)) errs.push('At least one special character');
  return errs;
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function PSPLogin({ onLogin, notice }) {
  // Reset brand colors synchronously — prevents bleed from photographer portal
  document.documentElement.style.setProperty('--brand',        '#0077B6');
  document.documentElement.style.setProperty('--brand-dark',   '#005F92');
  document.documentElement.style.setProperty('--brand-light',  'rgba(0,119,182,0.1)');
  document.documentElement.style.setProperty('--brand-border', 'rgba(0,119,182,0.3)');

  const [step, setStep] = useState('login'); // login | forgot | forgot-sent | reset | reset-done
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [newPassErrors, setNewPassErrors] = useState([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset brand colors to PSP defaults on mount
  useEffect(() => {
    document.documentElement.style.setProperty('--brand',        '#0077B6');
    document.documentElement.style.setProperty('--brand-dark',   '#005F92');
    document.documentElement.style.setProperty('--brand-light',  'rgba(0,119,182,0.1)');
    document.documentElement.style.setProperty('--brand-border', 'rgba(0,119,182,0.3)');
  }, []);

  // Read reset token from URL if present
  const resetToken = new URLSearchParams(window.location.search).get('token');
  useEffect(() => { if (resetToken) setStep('reset'); }, [resetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { const { data } = await pspLogin({ email, password }); onLogin(data.user); }
    catch (err) { setError(err.response?.data?.error || 'Invalid email or password'); }
    finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await pspForgotPassword(email); }
    catch { /* always proceed to avoid email enumeration */ }
    finally { setLoading(false); setStep('forgot-sent'); }
  };

  const handleReset = async (e) => {
    e.preventDefault(); setError('');
    const pwErrors = validatePwList(newPass);
    if (pwErrors.length) { setNewPassErrors(pwErrors); return; }
    if (newPass !== newPass2) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await pspResetPassword(resetToken, newPass);
      setInfo('Password reset! You can now sign in.');
      setStep('login');
      window.history.replaceState({}, '', '/psp');
    } catch (err) {
      setError(err.response?.data?.error || 'Reset link is invalid or has expired');
    } finally { setLoading(false); }
  };

  const card = (content) => (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--color-background-tertiary)' }}>
      <div style={{ width:380, background:'var(--color-background-primary)', borderRadius:'var(--border-radius-lg)', padding:32, boxShadow:'0 4px 24px rgba(0,0,0,0.08)', border:'0.5px solid var(--color-border-tertiary)' }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ width:48, height:48, borderRadius:12, background:'#2E3A59', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', fontSize:22 }}>🖨️</div>
          <div style={{ fontSize:20, fontWeight:500, color:'var(--color-text-primary)' }}>PSP Portal</div>
          <div style={{ fontSize:13, color:'var(--color-text-secondary)', marginTop:4 }}>Solidiphy Production</div>
        </div>
        {info && <div style={{ background:'#d1fae5', color:'#065f46', borderRadius:8, padding:'8px 12px', fontSize:13, marginBottom:14 }}>{info}</div>}
        {notice && !info && !error && <div style={{ background:'#FEF3C7', color:'#92400E', borderRadius:8, padding:'8px 12px', fontSize:13, marginBottom:14 }}>{notice}</div>}
        {error && <div style={{ background:'#FEE2E2', color:'#991B1B', borderRadius:8, padding:'8px 12px', fontSize:13, marginBottom:14 }}>{error}</div>}
        {content}
      </div>
    </div>
  );

  if (step === 'reset') return card(
    <form onSubmit={handleReset}>
      <div style={{ fontSize:15, fontWeight:500, marginBottom:14 }}>Set new password</div>
      <div className="field" style={{ marginBottom:14 }}>
        <label className="label">New password</label>
        <input className="input" type="password" value={newPass}
          onChange={e => { setNewPass(e.target.value); setNewPassErrors(validatePwList(e.target.value)); }}
          placeholder="Min 8 chars, upper, lower, special" required autoFocus />
        {newPassErrors.length > 0 && <ul style={{ margin:'6px 0 0 16px', padding:0, fontSize:12, color:'#991B1B' }}>{newPassErrors.map((e,i) => <li key={i}>{e}</li>)}</ul>}
      </div>
      <div className="field" style={{ marginBottom:20 }}>
        <label className="label">Confirm password</label>
        <input className="input" type="password" value={newPass2} onChange={e => setNewPass2(e.target.value)} placeholder="Re-enter your password" required />
      </div>
      <button className="btn btn-primary btn-full" disabled={loading}>{loading ? 'Saving…' : 'Reset password'}</button>
    </form>
  );

  if (step === 'forgot-sent') return card(
    <>
      <div style={{ textAlign:'center', fontSize:14, color:'var(--color-text-primary)', marginBottom:20 }}>
        ✉️ If that email address has an account, a reset link has been sent. Check your inbox.
      </div>
      <button className="btn btn-full" onClick={() => { setStep('login'); setError(''); }}>Back to sign in</button>
    </>
  );

  if (step === 'forgot') return card(
    <form onSubmit={handleForgot}>
      <div style={{ fontSize:15, fontWeight:500, marginBottom:14 }}>Reset password</div>
      <div className="field" style={{ marginBottom:20 }}>
        <label className="label">Email</label>
        <input className="input" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder="you@example.com" required autoFocus />
      </div>
      <button className="btn btn-primary btn-full" disabled={loading}>{loading ? 'Sending…' : 'Send reset link'}</button>
      <button type="button" className="btn btn-full" style={{ marginTop:8 }} onClick={() => { setStep('login'); setError(''); }}>Back to sign in</button>
    </form>
  );

  return card(
    <form onSubmit={handleSubmit}>
      <div className="field" style={{ marginBottom:14 }}>
        <label className="label">Email</label>
        <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus />
      </div>
      <div className="field" style={{ marginBottom:10 }}>
        <label className="label">Password</label>
        <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required />
      </div>
      <div style={{ textAlign:'right', marginBottom:20 }}>
        <button type="button" onClick={() => { setStep('forgot'); setError(''); setInfo(''); }}
          style={{ background:'none', border:'none', color:'var(--color-text-secondary)', fontSize:12, cursor:'pointer', textDecoration:'underline', padding:0 }}>
          Forgot password?
        </button>
      </div>
      <button className="btn btn-primary btn-full" disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</button>
    </form>
  );
}

// ── Change Password Screen ────────────────────────────────────────────────────
function ChangePasswordScreen({ user, onComplete }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    const pwErrors = validatePwList(password);
    if (pwErrors.length) { setErrors(pwErrors); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try { await pspChangePassword(password); onComplete(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to change password'); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--color-background-tertiary)' }}>
      <div style={{ width:420, background:'var(--color-background-primary)', borderRadius:'var(--border-radius-lg)', padding:32, boxShadow:'0 4px 24px rgba(0,0,0,0.08)', border:'0.5px solid var(--color-border-tertiary)' }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ width:48, height:48, borderRadius:12, background:'#2E3A59', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', fontSize:22 }}>🔒</div>
          <div style={{ fontSize:20, fontWeight:500, color:'var(--color-text-primary)' }}>Set your password</div>
          <div style={{ fontSize:13, color:'var(--color-text-secondary)', marginTop:6 }}>Welcome {user.name}! Please set a new password before continuing.</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="field" style={{ marginBottom:14 }}>
            <label className="label">New password</label>
            <input className="input" type="password" value={password} onChange={e => { setPassword(e.target.value); setErrors(validatePwList(e.target.value)); }} placeholder="Min 8 chars, upper, lower, special" required autoFocus />
            {errors.length > 0 && <ul style={{ margin:'6px 0 0 16px', padding:0, fontSize:12, color:'#991B1B' }}>{errors.map((e,i) => <li key={i}>{e}</li>)}</ul>}
          </div>
          <div className="field" style={{ marginBottom:20 }}>
            <label className="label">Confirm password</label>
            <input className="input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter your password" required />
          </div>
          {error && <div style={{ background:'#FEE2E2', color:'#991B1B', borderRadius:8, padding:'8px 12px', fontSize:13, marginBottom:14 }}>{error}</div>}
          <button className="btn btn-primary btn-full" disabled={loading || errors.length > 0}>{loading ? 'Saving…' : 'Set password & continue'}</button>
        </form>
      </div>
    </div>
  );
}

// ── Confirm Popup ─────────────────────────────────────────────────────────────
function ConfirmPopup({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#ffffff', borderRadius:'var(--border-radius-lg)', padding:28, width:380 }}>
        <div style={{ fontSize:15, fontWeight:500, color:'var(--color-text-primary)', marginBottom:10 }}>Confirm status change</div>
        <div style={{ fontSize:13, color:'var(--color-text-secondary)', marginBottom:20 }}>{message}</div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ── Line Item Files Panel ─────────────────────────────────────────────────────
function LineItemPanel({ orderId, item, toast, onStatusChange, openDownloadModal }) {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [partnerData, setPartnerData] = useState(null); // Phase 12

  // Use orderId prop first, fallback to item.order?.id
  const effectiveOrderId = orderId || item.order?.id;

  // Phase 12 — what this PSP reported via the Partner API (read-only here)
  useEffect(() => {
    if (!effectiveOrderId) return;
    getPSPItemPartnerData(effectiveOrderId, item.id).then(r => setPartnerData(r.data)).catch(() => setPartnerData(null));
  }, [effectiveOrderId, item.id]);

  useEffect(() => {
    if (!effectiveOrderId) { setLoading(false); return; }
    setLoading(true);
    getPSPItemFiles(effectiveOrderId, item.id)
      .then(r => setFiles(r.data))
      .catch(() => setFiles({ original:[], modeled:[] }))
      .finally(() => setLoading(false));
  }, [effectiveOrderId, item.id]);

  const handleDownload = (filePath, filename) => {
    const url = `${API_BASE}${pspDownloadUrl(filePath, filename)}`;
    window.open(url, '_blank');
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file);
        await uploadModeledFile(effectiveOrderId, item.id, fd);
      }
      toast(`${files.length} file${files.length > 1 ? 's' : ''} uploaded to Modeled folder`, 'success');
      const r = await getPSPItemFiles(effectiveOrderId, item.id); setFiles(r.data);
    } catch (err) { toast(err.response?.data?.error || 'Upload failed', 'error'); }
    finally { setUploading(false); }
  };

  const order = item.order;
  const shippingName = `${order?.shippingFirstName || ''} ${order?.shippingLastName || ''}`.trim() || `${order?.customerFirstName || ''} ${order?.customerLastName || ''}`.trim();

  const frontPhotoUrl = (item.photoOneDrivePath  && item.photoUrl  !== '#demo') ? `/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}`  : null;
  const backPhotoUrl  = (item.photoOneDrivePath2 && item.photoUrl2 !== '#demo') ? `/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath2)}` : null;

  return (
    <div style={{ padding:'12px 16px 16px', background:'var(--color-background-secondary)', borderTop:'0.5px solid var(--color-border-tertiary)' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:12, marginBottom:12 }}>
        {/* Photos (Phase 9) */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#ffffff' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Photos</div>
          <div style={{ display:'flex', gap:8 }}>
            {frontPhotoUrl && (
              <LazyImage src={frontPhotoUrl} alt="Front" onClick={() => setLightbox(frontPhotoUrl)}
                style={{ width:80, height:80, objectFit:'cover', borderRadius:6, border:'1px solid var(--color-border-tertiary)', cursor:'zoom-in' }} />
            )}
            {backPhotoUrl && (
              <LazyImage src={backPhotoUrl} alt="Back" onClick={() => setLightbox(backPhotoUrl)}
                style={{ width:80, height:80, objectFit:'cover', borderRadius:6, border:'1px solid var(--color-border-tertiary)', cursor:'zoom-in' }} />
            )}
            {!frontPhotoUrl && <div style={{ fontSize:12, color:'var(--color-text-secondary)', fontStyle:'italic' }}>No photos</div>}
          </div>
        </div>

        {/* Original files */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#ffffff' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>Original ({files?.original?.length || 0})</span>
            {files?.original?.length > 0 && (
              <button className="btn btn-sm btn-primary" onClick={() => openDownloadModal(files.original)} style={{ fontSize:11 }}>
                Download files
              </button>
            )}
          </div>
          {loading ? <Spinner size={16} /> : files?.original?.length > 0 ? (
            (files.original || []).map((f,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 8px', background:'var(--color-background-secondary)', borderRadius:'var(--border-radius-md)', marginBottom:4, border:'0.5px solid var(--color-border-tertiary)' }}>
                <span style={{ flex:1, fontSize:12, color:'var(--color-text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
              </div>
            ))
          ) : <div style={{ fontSize:12, color:'var(--color-text-secondary)', fontStyle:'italic' }}>No files yet</div>}
        </div>

        {/* Modeled files */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#ffffff' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>Modeled ({files?.modeled?.length || 0})</span>
            <label style={{ cursor:'pointer' }}>
              <input type="file" multiple style={{ display:'none' }} onChange={handleUpload} disabled={uploading} />
              <span className="btn btn-sm btn-primary" style={{ fontSize:11 }}>
                {uploading ? 'Uploading…' : 'Upload to Modeled'}
              </span>
            </label>
          </div>
          {loading ? <Spinner size={16} /> : files?.modeled?.length > 0 ? files.modeled.map((f,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 8px', background:'var(--color-background-secondary)', borderRadius:'var(--border-radius-md)', marginBottom:4, border:'0.5px solid var(--color-border-tertiary)' }}>
              <span style={{ flex:1, fontSize:12, color:'var(--color-text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
            </div>
          )) : <div style={{ fontSize:12, color:'var(--color-text-secondary)', fontStyle:'italic' }}>No files yet</div>}
        </div>

        {/* Shipping address + line item info */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#ffffff' }}>
          <div style={{ fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
            <i className="ti ti-map-pin" style={{ marginRight:4 }} aria-hidden="true"></i>Ship to
          </div>
          <div style={{ fontSize:12, color:'var(--color-text-primary)', lineHeight:1.6 }}>
            <div style={{ fontWeight:500 }}>{shippingName}</div>
            {order?.roomNumber && <div style={{ color:'var(--color-text-secondary)' }}>Room: {order.roomNumber}</div>}
            {order?.shippingAddress1 && <>
              <div>{order.shippingAddress1}</div>
              {order.shippingAddress2 && <div>{order.shippingAddress2}</div>}
              <div>{order.shippingCity}{order.shippingProvince ? `, ${order.shippingProvince}` : ''} {order.shippingZip}</div>
              <div>{order.shippingCountry}</div>
            </>}
            {order?.customerPhone && <div style={{ color:'var(--color-text-secondary)', marginTop:4 }}>{order.customerPhone}</div>}
          </div>
          <div style={{ marginTop:10, padding:'8px 10px', background:'var(--color-background-primary)', borderRadius:'var(--border-radius-md)', border:'0.5px solid var(--color-border-tertiary)', fontSize:11, fontFamily:'monospace', color:'var(--color-text-secondary)', lineHeight:1.7 }}>
            <div><span style={{ color:'var(--color-text-secondary)' }}>Ref:</span> <span style={{ color:'var(--color-text-primary)', fontWeight:500 }}>{item.referenceNumber || '—'}</span></div>
            <div><span style={{ color:'var(--color-text-secondary)' }}>Item ID:</span> <span style={{ color:'var(--color-text-primary)' }}>{item.lineItemDescription}</span></div>
            <div><span style={{ color:'var(--color-text-secondary)' }}>SKU:</span> <span style={{ color:'var(--color-text-primary)' }}>{item.sku}</span></div>
            <div><span style={{ color:'var(--color-text-secondary)' }}>Qty:</span> <span style={{ color:'var(--color-text-primary)' }}>{item.quantity}</span></div>
          </div>
        </div>
      </div>
      {effectiveOrderId && (
        <PSPModels load={() => getPSPItemModels(effectiveOrderId, item.id)}
          downloadUrl={(path, name) => `${API_BASE}${pspDownloadUrl(path, name)}`} />
      )}
      <PartnerData data={partnerData} />
      {lightbox && <PSPLightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

// ── Image lightbox (Phase 9) ───────────────────────────────────────────────────
function PSPLightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, cursor:'zoom-out' }}>
      <img src={src} alt="Preview" style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:8 }} onClick={e => e.stopPropagation()} />
      <button onClick={onClose} style={{ position:'absolute', top:20, right:24, background:'none', border:'none', color:'#fff', fontSize:28, cursor:'pointer' }}>✕</button>
    </div>
  );
}

// ── Table header ──────────────────────────────────────────────────────────────
function TableHeader({ allSelected, someSelected, onToggleAll, selectableCount }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 16px', background:'var(--color-background-secondary)', borderBottom:'0.5px solid var(--color-border-tertiary)', position:'sticky', top:0, zIndex:10 }}>
      <div style={{ width:12, flexShrink:0 }}></div>
      <div style={{ width:16, flexShrink:0 }}>
        {selectableCount > 0 && (
          <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
            onChange={onToggleAll}
            style={{ width:14, height:14, cursor:'pointer', accentColor:'#2E3A59' }} />
        )}
      </div>
      <div style={{ width:28, flexShrink:0 }}></div>
      <div style={{ flex:1, fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Item / Order</div>
      <div style={{ width:90, fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>Order Date</div>
      <div style={{ width:90, fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', textAlign:'right', flexShrink:0 }}>Last Modified</div>
      <div style={{ width:70, fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', textAlign:'right', flexShrink:0 }}>Items</div>
      <div style={{ width:130, fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>Status</div>
      <div style={{ width:200, fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>Actions</div>
    </div>
  );
}

// ── Line Item Row ─────────────────────────────────────────────────────────────
const LineItemRow = React.memo(function LineItemRow({ item, depth, toast, onRefresh, openDownloadModal, selected, onToggleSelect, onOptimisticUpdate }) {
  const { isAdmin } = React.useContext(PSPAdminContext);
  const [expanded, setExpanded] = useState(false);
  const [rowLightbox, setRowLightbox] = useState(null);
  const [showQCFail, setShowQCFail] = useState(false);
  const orderId = item.order?.id;
  const isFrozen = item.itemStatus === 'shipped_to_solidiphy';
  const isQC = item.itemStatus === 'qc';
  const availableStatuses = PSP_ITEM_TRANSITIONS[item.itemStatus] || [];

  const handleStatusChange = async (newStatus) => {
    const prevStatus = item.itemStatus;
    onOptimisticUpdate(item.id, { itemStatus: newStatus }); // instant UI update — don't wait on the network
    try {
      await updatePSPItemStatus(orderId, item.id, newStatus);
      toast(`Status → ${STATUS_LABELS[newStatus]}`, 'success');
      onRefresh(); // background reconcile (order-level roll-up, etc.)
    } catch (err) {
      onOptimisticUpdate(item.id, { itemStatus: prevStatus }); // roll back on failure
      toast(err.response?.data?.error || 'Failed', 'error');
    }
  };

  const handleQC = async (result, reason) => {
    const prevStatus = item.itemStatus;
    const targetStatus = result === 'pass' ? 'packing' : QC_FAIL_TARGET[reason];
    setShowQCFail(false);
    if (targetStatus) onOptimisticUpdate(item.id, { itemStatus: targetStatus }); // instant UI update
    try {
      await qcDecisionPSP(orderId, item.id, result, reason);
      toast(result === 'pass' ? 'QC Passed → Packing' : `QC Failed → ${reason}`, 'success');
      onRefresh();
    } catch (err) {
      onOptimisticUpdate(item.id, { itemStatus: prevStatus }); // roll back on failure
      toast(err.response?.data?.error || 'Failed', 'error');
    }
  };

  const indent = 16 + depth * 20;
  const baseBg = depthBg(depth);
  const photoUrl = (item.photoOneDrivePath && item.photoUrl !== '#demo') ? `/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}` : null;

  return (
    <>
      <div onClick={() => setExpanded(!expanded)}
        style={{ display:'flex', alignItems:'center', gap:10, padding:`8px 16px 8px ${indent}px`,
          cursor:'pointer', borderTop:'0.5px solid var(--color-border-tertiary)',
          background: baseBg, filter: expanded ? 'brightness(0.97)' : 'none' }}
        onMouseEnter={e => e.currentTarget.style.filter='brightness(0.97)'}
        onMouseLeave={e => e.currentTarget.style.filter = expanded ? 'brightness(0.97)' : 'none'}>
        <span style={{ fontSize:14, color:'var(--color-text-secondary)', flexShrink:0, userSelect:'none' }}>{expanded ? '▼' : '▶'}</span>
        <div style={{ width:16, flexShrink:0 }} onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={selected} disabled={isFrozen} onChange={() => onToggleSelect(item.id)}
            style={{ width:14, height:14, cursor: isFrozen ? 'not-allowed' : 'pointer', accentColor:'#2E3A59' }} />
        </div>
        {item.productImageUrl
          ? <LazyImage src={item.productImageUrl} alt="" style={{ width:28, height:28, objectFit:'contain', borderRadius:4, border:'0.5px solid var(--color-border-tertiary)', flexShrink:0 }} />
          : <div style={{ width:28, height:28, flexShrink:0 }}></div>}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:500, color:'var(--color-text-primary)', display:'flex', alignItems:'center', gap:6 }}>
            {item.productName}
            {item.order?.isRush && <RushBadge />}
            {isAdmin && item.assignedPspCompany && (
              <span style={{ fontSize:10, fontWeight:600, padding:'1px 7px', borderRadius:8, background:'#E0E7FF', color:'#3730A3', whiteSpace:'nowrap' }}>
                {item.assignedPspCompany.name}
              </span>
            )}
          </div>
          <div style={{ fontSize:11, color:'var(--color-text-secondary)', fontFamily:'monospace' }}>
            {item.lineItemDescription}
          </div>
        </div>
        {/* Photo thumbnail (Phase 9) — click to enlarge */}
        {photoUrl && (
          <LazyImage src={photoUrl} alt="Photo" onClick={e => { e.stopPropagation(); setRowLightbox(photoUrl); }}
            style={{ width:56, height:56, objectFit:'cover', borderRadius:6, border:'0.5px solid var(--color-border-tertiary)', flexShrink:0, cursor:'zoom-in' }} />
        )}
        <div style={{ width:90, fontSize:11, color:'var(--color-text-secondary)', flexShrink:0 }}>{fmtDate(item.order?.createdAt)}</div>
        <div style={{ width:90, fontSize:11, color:'var(--color-text-secondary)', textAlign:'right', flexShrink:0 }}>{fmtDate(item.updatedAt || item.order?.updatedAt)}</div>
        <div style={{ width:70, fontSize:11, color:'var(--color-text-secondary)', textAlign:'right', flexShrink:0 }}>Qty: {item.quantity}</div>
        <div style={{ width:130, flexShrink:0 }}>
          <Badge status={item.itemStatus} />
          {item.itemStatus === 'sculpting' && item.stageDetail && (
            <div style={{ fontSize:10, color:'var(--color-text-secondary)', marginTop:2 }}>{STAGE_LABEL[item.stageDetail]}</div>
          )}
        </div>
        <div style={{ width:200, flexShrink:0 }} onClick={e => e.stopPropagation()}>
          {isQC ? (
            <div style={{ display:'flex', gap:6 }}>
              <button className="btn btn-sm" onClick={() => handleQC('pass')}
                style={{ background:'#059669', color:'#fff', border:'none', fontSize:12, padding:'4px 10px' }}>✓ Pass</button>
              <button className="btn btn-sm" onClick={() => setShowQCFail(true)}
                style={{ background:'#DC2626', color:'#fff', border:'none', fontSize:12, padding:'4px 10px' }}>✗ Fail</button>
            </div>
          ) : !isFrozen && availableStatuses.length > 0 ? (
            <select className="input" style={{ fontSize:12, padding:'4px 8px', width:'100%' }}
              value="" onChange={e => { if (e.target.value) handleStatusChange(e.target.value); }}>
              <option value="">Change status…</option>
              {availableStatuses.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          ) : isFrozen ? (
            <span style={{ fontSize:11, color:'var(--color-text-secondary)', fontStyle:'italic' }}>Shipped — frozen</span>
          ) : null}
        </div>
      </div>
      {expanded && <LineItemPanel orderId={orderId} item={item} toast={toast} onStatusChange={handleStatusChange} openDownloadModal={openDownloadModal} />}
      {rowLightbox && <PSPLightbox src={rowLightbox} onClose={() => setRowLightbox(null)} />}
      {showQCFail && <QCFailModal onClose={() => setShowQCFail(false)} onSubmit={(reason) => handleQC('fail', reason)} />}
    </>
  );
}, (prev, next) => {
  // Custom comparator (Phase 9 perf): skip re-render unless something that
  // actually affects this row's output changed. The refreshed `item` is a
  // new object reference every fetch even when nothing in it differs, so a
  // plain shallow-props comparison (what React.memo does by default) would
  // never skip a re-render — this compares the fields that matter instead.
  const a = prev.item, b = next.item;
  return (
    a.id === b.id &&
    a.itemStatus === b.itemStatus &&
    a.photoOneDrivePath === b.photoOneDrivePath &&
    a.photoOneDrivePath2 === b.photoOneDrivePath2 &&
    a.photoUrl === b.photoUrl &&
    a.packingSlipId === b.packingSlipId &&
    a.updatedAt === b.updatedAt &&
    a.quantity === b.quantity &&
    a.order?.isRush === b.order?.isRush &&
    prev.depth === next.depth &&
    prev.selected === next.selected
  );
});

// ── QC Fail reason modal ───────────────────────────────────────────────────────
function QCFailModal({ onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1600 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:12, padding:22, width:340, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize:15, fontWeight:700, marginBottom:14 }}>QC Fail — Reject Reason</div>
        {QC_FAIL_REASONS.map(r => (
          <label key={r} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 4px', fontSize:13, cursor:'pointer' }}>
            <input type="radio" name="qcFailReason" checked={reason === r} onChange={() => setReason(r)} />
            {r}
          </label>
        ))}
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!reason} onClick={() => onSubmit(reason)}>Submit</button>
        </div>
      </div>
    </div>
  );
}

// ── Group Node (recursive) ────────────────────────────────────────────────────
function GroupNode({ label, sublabel, count, depth, children, badge }) {
  const [expanded, setExpanded] = useState(true); // expanded by default
  const indent = depth * 20;

  // Determine background shade based on depth
  const bg = depthBg(depth);

  return (
    <>
      <div onClick={() => setExpanded(!expanded)}
        style={{ display:'flex', alignItems:'center', gap:10, padding:`10px 16px 10px ${16 + indent}px`,
          cursor:'pointer', borderTop:'0.5px solid var(--color-border-tertiary)', background:bg }}
        onMouseEnter={e => e.currentTarget.style.filter='brightness(0.97)'}
        onMouseLeave={e => e.currentTarget.style.filter=''}>
        <span style={{ fontSize:14, color:'var(--color-text-secondary)', flexShrink:0, userSelect:'none' }}>{expanded ? '▼' : '▶'}</span>
        <div style={{ width:16, flexShrink:0 }}></div>
        <div style={{ width:28, flexShrink:0 }}></div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:500, color:'var(--color-text-primary)' }}>{label}</div>
          {sublabel && <div style={{ fontSize:11, color:'var(--color-text-secondary)' }}>{sublabel}</div>}
        </div>
        {badge && <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:'#DBEAFE', color:'#1E40AF', flexShrink:0 }}>{badge}</span>}
        <span style={{ width:90, fontSize:11, color:'var(--color-text-secondary)', flexShrink:0 }}></span>
        <span style={{ width:90, fontSize:11, color:'var(--color-text-secondary)', textAlign:'right', flexShrink:0 }}></span>
        <span style={{ width:70, fontSize:11, color:'var(--color-text-secondary)', textAlign:'right', flexShrink:0 }}>{count} item{count !== 1 ? 's' : ''}</span>
        <div style={{ width:130, flexShrink:0 }}></div>
        <div style={{ width:200, flexShrink:0 }}></div>
      </div>
      {expanded && children}
    </>
  );
}

// ── Order Node with STS button ────────────────────────────────────────────────
function OrderNode({ orderNumber, companyName, groupName, orderId, items, depth, toast, onRefresh, openDownloadModal, selectedIds, onToggleSelect, onOptimisticUpdate }) {
  const [expanded, setExpanded] = useState(true); // expanded by default
  const [showShipModal, setShowShipModal] = useState(false);
  const indent = depth * 20;

  const pspItems = items.filter(i => PSP_ITEM_STATUSES.includes(i.itemStatus));
  const packingItems = pspItems.filter(i => i.itemStatus === 'packing');
  const allInPacking = pspItems.length > 0 && pspItems.every(i => i.itemStatus === 'packing');
  const isRush = items[0]?.order?.isRush;

  const bg = depthBg(depth);

  return (
    <>
      {showShipModal && (
        <ShipToSolidiphyModal
          orderId={orderId}
          packingItems={packingItems}
          toast={toast}
          onClose={() => setShowShipModal(false)}
          onShipped={() => { setShowShipModal(false); onRefresh(); }}
        />
      )}
      <div onClick={() => setExpanded(!expanded)}
        style={{ display:'flex', alignItems:'center', gap:10, padding:`10px 16px 10px ${16 + indent}px`,
          cursor:'pointer', borderTop:'0.5px solid var(--color-border-tertiary)', background:bg }}
        onMouseEnter={e => e.currentTarget.style.filter='brightness(0.97)'}
        onMouseLeave={e => e.currentTarget.style.filter=''}>
        <span style={{ fontSize:14, color:'var(--color-text-secondary)', flexShrink:0, userSelect:'none' }}>{expanded ? '▼' : '▶'}</span>
        <div style={{ width:16, flexShrink:0 }}></div>
        <div style={{ width:28, flexShrink:0 }}></div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:500, color:'var(--color-text-primary)', fontFamily:'monospace', display:'flex', alignItems:'center', gap:6 }}>
            {orderNumber}
            {isRush && <RushBadge />}
          </div>
          <div style={{ fontSize:11, color:'var(--color-text-secondary)' }}>{companyName} · {groupName}</div>
        </div>
        <span style={{ width:90, fontSize:11, color:'var(--color-text-secondary)', flexShrink:0 }}>{fmtDate(items[0]?.order?.createdAt)}</span>
        <span style={{ width:90, fontSize:11, color:'var(--color-text-secondary)', textAlign:'right', flexShrink:0 }}></span>
        <span style={{ width:70, fontSize:11, color:'var(--color-text-secondary)', textAlign:'right', flexShrink:0 }}>{pspItems.length} item{pspItems.length !== 1 ? 's' : ''}</span>
        <div style={{ width:130, flexShrink:0 }}></div>
        <div style={{ width:200, flexShrink:0 }}>
          {allInPacking && (
            <button className="btn btn-sm btn-primary" onClick={e => { e.stopPropagation(); setShowShipModal(true); }} style={{ fontSize:11 }}>
              <i className="ti ti-truck" style={{ marginRight:4 }} aria-hidden="true"></i>Ship to Solidiphy
            </button>
          )}
        </div>
      </div>
      {expanded && pspItems.map(item => (
        <LineItemRow key={item.id} item={item} depth={depth + 1} toast={toast} onRefresh={onRefresh} openDownloadModal={openDownloadModal}
          selected={selectedIds.includes(item.id)} onToggleSelect={onToggleSelect} onOptimisticUpdate={onOptimisticUpdate} />
      ))}
    </>
  );
}

// ── Ship to Solidiphy modal (Phase 9) ──────────────────────────────────────────
// Clicking "Ship to Solidiphy" now shows the packing slip Print/Reprint flow —
// completing print/reprint is what actually advances the order to STS.
// Cancel closes with no status change (order stays in Packing).
function ShipToSolidiphyModal({ orderId, packingItems, toast, onClose, onShipped }) {
  const { isAdmin, pspFilter } = React.useContext(PSPAdminContext);
  const [submitting, setSubmitting] = useState(false);
  const slipIds = [...new Set(packingItems.map(i => i.packingSlipId).filter(Boolean))];
  const allOnSameSlip = slipIds.length === 1 && packingItems.every(i => i.packingSlipId === slipIds[0]);
  const isClean = packingItems.every(i => !i.packingSlipId);
  const isMixed = !allOnSameSlip && !isClean;

  const finishShipping = async () => {
    setSubmitting(true);
    try {
      // Admin with a PSP filter ships only that PSP's portion of the order
      await updatePSPOrderStatus(orderId, 'shipped_to_solidiphy', isAdmin && pspFilter ? { pspCompanyId: pspFilter } : {});
      toast('Marked as Shipped to Solidiphy', 'success');
      onShipped();
    } catch (err) {
      toast(err.response?.data?.error || 'Failed', 'error');
      setSubmitting(false);
    }
  };

  const handlePrint = async () => {
    setSubmitting(true);
    try {
      const { data } = await createPackingSlip(packingItems.map(i => i.id));
      printPackingSlip(packingItems, data.slipId);
      await finishShipping();
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to create packing slip', 'error');
      setSubmitting(false);
    }
  };

  const handleReprint = async () => {
    printPackingSlip(packingItems, slipIds[0]);
    await finishShipping();
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1800, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:12, padding:24, width:400, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize:16, fontWeight:700, marginBottom:8 }}>Ship to Solidiphy</div>
        {isMixed ? (
          <div style={{ fontSize:13, color:'#B45309', marginBottom:16 }}>
            These items are on different packing slips. Please reconcile packing slips for this order (via the queue's bulk selection) before shipping.
          </div>
        ) : (
          <div style={{ fontSize:13, color:'var(--color-text-secondary)', marginBottom:16 }}>
            {isClean
              ? `This will print a packing slip for ${packingItems.length} item(s) and mark the order as Shipped to Solidiphy.`
              : `This will reprint packing slip ${slipIds[0]} for ${packingItems.length} item(s) and mark the order as Shipped to Solidiphy.`}
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
          {!isMixed && (
            <button className="btn btn-primary" disabled={submitting} onClick={isClean ? handlePrint : handleReprint}>
              <i className="ti ti-printer" style={{ marginRight:4 }} aria-hidden="true"></i>
              {submitting ? 'Working…' : (isClean ? 'Print Packing Slip' : 'Reprint Packing Slip')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Grouped List (recursive grouping engine) ──────────────────────────────────
function GroupedList({ items, groupLevels, depth = 0, toast, onRefresh, openDownloadModal, selectedIds, onToggleSelect, onOptimisticUpdate }) {
  if (!items || items.length === 0) return null;
  if (groupLevels.length === 0) {
    return items.map(item => (
      <LineItemRow key={item.id} item={item} depth={depth} toast={toast} onRefresh={onRefresh} openDownloadModal={openDownloadModal}
        selected={selectedIds.includes(item.id)} onToggleSelect={onToggleSelect} onOptimisticUpdate={onOptimisticUpdate} />
    ));
  }

  const [currentLevel, ...remainingLevels] = groupLevels;
  const grouped = {};
  items.forEach(item => {
    let key, label, sublabel;
    if (currentLevel === 'company') { key = item.order?.companyName || 'Unknown'; label = key; }
    else if (currentLevel === 'group') { key = item.order?.groupName || 'Unknown'; label = key; sublabel = item.order?.companyName; }
    else if (currentLevel === 'sku') { key = item.sku; label = item.productName; sublabel = `SKU: ${item.sku}`; }
    else if (currentLevel === 'order') { key = item.order?.orderNumber || 'Unknown'; label = key; sublabel = `${item.order?.companyName} · ${item.order?.groupName}`; }
    if (!grouped[key]) grouped[key] = { label, sublabel, items: [], orderId: item.order?.id, orderNumber: item.order?.orderNumber, companyName: item.order?.companyName, groupName: item.order?.groupName };
    grouped[key].items.push(item);
  });

  return Object.entries(grouped).map(([key, group]) => {
    if (currentLevel === 'order' && remainingLevels.length === 0) {
      return (
        <OrderNode key={key} orderNumber={group.orderNumber} companyName={group.companyName}
          groupName={group.groupName} orderId={group.orderId} items={group.items}
          depth={depth} toast={toast} onRefresh={onRefresh} openDownloadModal={openDownloadModal}
          selectedIds={selectedIds} onToggleSelect={onToggleSelect} onOptimisticUpdate={onOptimisticUpdate} />
      );
    }
    return (
      <GroupNode key={key} label={group.label} sublabel={group.sublabel} count={group.items.length} depth={depth}>
        <GroupedList items={group.items} groupLevels={remainingLevels} depth={depth + 1} toast={toast} onRefresh={onRefresh} openDownloadModal={openDownloadModal}
          selectedIds={selectedIds} onToggleSelect={onToggleSelect} onOptimisticUpdate={onOptimisticUpdate} />
      </GroupNode>
    );
  });
}

// ── Main PSP Screen ───────────────────────────────────────────────────────────
export default function PSPScreen() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [groupBy, setGroupBy] = useState(['company','group','order']); // default grouping
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]); // Phase 9 — multi-select
  const [companyFilter, setCompanyFilter] = useState('');
  const [rushOnly, setRushOnly] = useState(false); // Phase 9
  const [sortDir, setSortDir] = useState('desc'); // desc = newest first
  const [hideSTS, setHideSTS] = useState(true); // hide STS by default
  const [datePreset, setDatePreset] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const getDateRange = (preset) => {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'today') return { from: fmt(now), to: fmt(now) };
    if (preset === 'week') {
      const mon = new Date(now); mon.setDate(now.getDate() - now.getDay() + 1);
      return { from: fmt(mon), to: fmt(now) };
    }
    if (preset === 'month') {
      return { from: `${now.getFullYear()}-${pad(now.getMonth()+1)}-01`, to: fmt(now) };
    }
    return { from: '', to: '' };
  };
  const [downloadModal, setDownloadModal] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTarget, setBulkTarget] = useState('');
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [activeView, setActiveView] = useState(() => sessionStorage.getItem('psp_tab') || 'queue'); // 'queue' | 'slips'
  const [slips, setSlips] = useState([]);
  const [slipsLoading, setSlipsLoading] = useState(false);
  const [adminMode, setAdminMode] = useState(false);      // Phase 12 — admin view
  const [pspFilter, setPspFilter] = useState('');         // '' = all PSP companies
  const [pspCompanies, setPspCompanies] = useState([]);
  const [loginNotice, setLoginNotice] = useState(''); // shown on the login screen after a lost session
  const { toast } = useToast();

  // Login lost mid-session (e.g. signed out in another tab — the PSP and
  // photographer portals share one browser login). Any 401 from /api/psp/*
  // sends the user back to sign in with a clear message instead of leaving a
  // stale screen that fails on every click. Admin view → back to admin login.
  const adminModeRef = React.useRef(false);
  useEffect(() => { adminModeRef.current = adminMode; }, [adminMode]);
  useEffect(() => {
    const id = api.interceptors.response.use(r => r, err => {
      const url = err.config?.url || '';
      if (err.response?.status === 401 && /^\/psp\//.test(url)) {
        if (adminModeRef.current) {
          sessionStorage.removeItem('psp_admin_view');
          setPSPAdminView(false);
          window.location.href = '/admin';
        } else {
          setLoginNotice('Your session has ended — please sign in again.');
          setUser(null);
        }
      }
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    document.title = 'PSP Portal — Solidiphy';
    // Admin view: opened from the admin portal (/psp?admin=1); remembered for
    // this tab so a refresh stays in admin view.
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === '1') {
      sessionStorage.setItem('psp_admin_view', '1');
      params.delete('admin');
      window.history.replaceState(null, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    }
    const pspLoginFlow = () => pspMe().then(r => setUser(r.data.user)).catch(() => setUser(null));
    if (sessionStorage.getItem('psp_admin_view') === '1') {
      adminUserMe()
        .then(r => {
          setPSPAdminView(true);
          setAdminMode(true);
          setUser({ name: r.data.name, email: r.data.email, isAdmin: true });
          getPSPCompanies().then(c => setPspCompanies(c.data || [])).catch(() => {});
        })
        .catch(() => { sessionStorage.removeItem('psp_admin_view'); return pspLoginFlow(); }) // admin session gone → normal PSP login
        .finally(() => setAuthLoading(false));
    } else {
      pspLoginFlow().finally(() => setAuthLoading(false));
    }
  }, []);

  const loadItems = useCallback(() => {
    setLoading(true);
    const range = datePreset ? getDateRange(datePreset) : { from: dateFrom, to: dateTo };
    getPSPLineItems({ search, status: statusFilter.join(','), company: companyFilter, sortDir, hideSTS, rushOnly: rushOnly ? 'true' : '', dateFrom: range.from, dateTo: range.to,
      ...(adminMode && pspFilter ? { pspCompanyId: pspFilter } : {}) })
      .then(r => setLineItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLineItems([]))
      .finally(() => setLoading(false));
    setSelectedIds([]);
  }, [search, statusFilter, companyFilter, sortDir, hideSTS, rushOnly, datePreset, dateFrom, dateTo, adminMode, pspFilter]);

  const loadSlips = useCallback(() => {
    setSlipsLoading(true);
    getPSPPackingSlips()
      .then(r => setSlips(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSlips([]))
      .finally(() => setSlipsLoading(false));
  }, []);

  useEffect(() => {
    if (user && !user.mustChangePassword) {
      loadItems();
      // Phase 9 fix — activeView can restore straight to 'slips' from
      // sessionStorage on a refresh, bypassing the nav button's onClick
      // (which is normally what triggers loadSlips()). Cover that one-time
      // case here without depending on the reactive activeView state,
      // which would otherwise re-fetch on every subsequent tab switch too.
      if (sessionStorage.getItem('psp_tab') === 'slips') loadSlips();
    }
  }, [user, loadItems, loadSlips]);

  const toggleGroup = (id) => {
    setGroupBy(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const handleLogout = () => {
    if (adminMode) { // leave admin view — don't end the admin session
      sessionStorage.removeItem('psp_admin_view');
      setPSPAdminView(false);
      window.location.href = '/admin';
      return;
    }
    pspLogout().finally(() => setUser(null));
  };

  // Phase 9 perf — instantly reflect a status change locally instead of
  // waiting on the PATCH + refetch round trip; onRefresh() still runs in the
  // background afterward to reconcile (order-level status roll-up, etc.)
  // NOTE: must stay above the early returns below — hooks can't be called
  // conditionally, and those returns fire before `user` is loaded.
  const optimisticUpdate = useCallback((itemId, patch) => {
    setLineItems(prev => prev.map(i => i.id === itemId ? { ...i, ...patch } : i));
  }, []);

  if (authLoading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}><Spinner /></div>;
  // If a reset token is in the URL, always show the reset form regardless of session
  if (new URLSearchParams(window.location.search).get('token')) return <PSPLogin onLogin={u => setUser(u)} />;
  if (!user) return <PSPLogin notice={loginNotice} onLogin={u => { setLoginNotice(''); setUser(u); }} />;
  if (user.mustChangePassword) return <ChangePasswordScreen user={user} onComplete={() => setUser({ ...user, mustChangePassword: false })} />;

  // Enforce fixed hierarchy order: company → group → sku → order
  const openDownloadModal = (fileList) => {
    setSelectedFiles(fileList.map(f => f.path));
    setDownloadModal(fileList);
  };

  const handleDownloadSelected = () => {
    selectedFiles.forEach((path, index) => {
      setTimeout(() => {
        const filename = path.split('/').pop();
        const url = `${API_BASE}${pspDownloadUrl(path, filename)}`;
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        setTimeout(() => document.body.removeChild(iframe), 10000);
      }, index * 800);
    });
    setDownloadModal(null);
  };

  const toggleFile = (path) => {
    setSelectedFiles(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };

  // ── Bulk selection helpers ────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectableItems = lineItems.filter(i => i.itemStatus !== 'shipped_to_solidiphy');
  const allSelected = selectableItems.length > 0 && selectableItems.every(i => selectedIds.includes(i.id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(selectableItems.map(i => i.id));
  };

  const selectedItems = lineItems.filter(i => selectedIds.includes(i.id));
  const selectedStatuses = [...new Set(selectedItems.map(i => i.itemStatus))];
  const commonStatus = selectedStatuses.length === 1 ? selectedStatuses[0] : null;
  const bulkTargetOptions = commonStatus ? (PSP_ITEM_TRANSITIONS[commonStatus] || []) : [];
  const packingSelected = selectedItems.filter(i => i.itemStatus === 'packing');

  const handleBulkApply = () => {
    if (!bulkTarget || !commonStatus) return;
    const idsToUpdate = [...selectedIds]; // snapshot before clearing selection
    setBulkConfirm({
      message: `Change ${selectedItems.length} item(s) from ${STATUS_LABELS[commonStatus]} to ${STATUS_LABELS[bulkTarget]}?`,
      onConfirm: async () => {
        setBulkConfirm(null);
        // Instant UI update — don't wait on the network for the whole batch
        idsToUpdate.forEach(id => optimisticUpdate(id, { itemStatus: bulkTarget }));
        setSelectedIds([]);
        setBulkTarget('');
        try {
          const r = await bulkUpdatePSPItemStatus(idsToUpdate, bulkTarget);
          toast(`${r.data.updated} item(s) → ${STATUS_LABELS[bulkTarget]}`, 'success');
          loadItems();
        } catch (err) {
          // Roll back — reload from server since we don't know the pre-bulk
          // per-item statuses anymore once they've been mixed together
          toast(err.response?.data?.error || 'Failed', 'error');
          loadItems();
        }
      }
    });
  };

  // Derive unique companies from loaded items for filter dropdown
  const companies = [...new Set(lineItems.map(i => i.order?.companyName).filter(Boolean))].sort();

  const orderedGroupBy = GROUP_OPTIONS.map(o => o.id).filter(id => groupBy.includes(id));

  return (
    <PSPAdminContext.Provider value={{ isAdmin: adminMode, pspFilter }}>
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--color-background-tertiary)' }}>
      <Toasts />

      {/* Download selection modal — rendered at top level to avoid overflow clipping */}
      {downloadModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#ffffff', borderRadius:'var(--border-radius-lg)', padding:24, width:440, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <div style={{ fontSize:15, fontWeight:500, color:'var(--color-text-primary)' }}>Select files to download</div>
              <button className="btn btn-sm" onClick={() => setDownloadModal(null)}>✕</button>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:'var(--color-text-secondary)', marginBottom:8, paddingBottom:8, borderBottom:'0.5px solid var(--color-border-tertiary)' }}>
              <input type="checkbox"
                checked={selectedFiles.length === downloadModal.length}
                onChange={() => setSelectedFiles(selectedFiles.length === downloadModal.length ? [] : downloadModal.map(f => f.path))}
                style={{ width:14, height:14, accentColor:'#2E3A59' }} />
              Select all ({downloadModal.length} file{downloadModal.length !== 1 ? 's' : ''})
            </label>
            <div style={{ marginBottom:16 }}>
              {downloadModal.map((f, i) => (
                <label key={i} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'7px 0', borderBottom:'0.5px solid var(--color-border-tertiary)' }}>
                  <input type="checkbox" checked={selectedFiles.includes(f.path)} onChange={() => toggleFile(f.path)}
                    style={{ width:14, height:14, accentColor:'#2E3A59', flexShrink:0 }} />
                  <i className="ti ti-file" style={{ fontSize:13, color:'var(--color-text-secondary)', flexShrink:0 }} aria-hidden="true"></i>
                  <span style={{ flex:1, fontSize:13, color:'var(--color-text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                </label>
              ))}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn" onClick={() => setDownloadModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={selectedFiles.length === 0} onClick={handleDownloadSelected}>
                <i className="ti ti-download" style={{ marginRight:4 }} aria-hidden="true"></i>
                Download {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div style={{ width:220, background:'#2E3A59', flexShrink:0, display:'flex', flexDirection:'column', height:'100vh', position:'sticky', top:0, overflowY:'auto' }}>
        <div style={{ padding:'20px 16px 16px', borderBottom:'0.5px solid rgba(255,255,255,0.12)' }}>
          <div style={{ width:36, height:36, borderRadius:8, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, marginBottom:10 }}>🖨️</div>
          <div style={{ fontSize:14, fontWeight:500, color:'#fff' }}>PSP Portal</div>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 }}>Solidiphy Production</div>
          {adminMode && (
            <div style={{ display:'inline-block', marginTop:8, fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:8, background:'#FCD34D', color:'#78350F' }}>
              Admin view — all PSPs
            </div>
          )}
        </div>
        <nav style={{ flex:1, padding:'8px 0' }}>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', padding:'12px 16px 4px', letterSpacing:'0.08em', textTransform:'uppercase' }}>Work Queue</div>
          <button style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 16px', background: activeView==='queue' ? 'rgba(255,255,255,0.12)' : 'none', borderLeft: activeView==='queue' ? '3px solid #fff' : '3px solid transparent', border:'none', cursor:'pointer', fontSize:13, color:'#fff', textAlign:'left' }}
            onClick={() => { setActiveView('queue'); sessionStorage.setItem('psp_tab', 'queue'); }}>
            <i className="ti ti-list" style={{ fontSize:16 }} aria-hidden="true"></i>Orders
          </button>
          <button style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 16px', background: activeView==='slips' ? 'rgba(255,255,255,0.12)' : 'none', borderLeft: activeView==='slips' ? '3px solid #fff' : '3px solid transparent', border:'none', cursor:'pointer', fontSize:13, color:'#fff', textAlign:'left' }}
            onClick={() => { setActiveView('slips'); sessionStorage.setItem('psp_tab', 'slips'); loadSlips(); }}>
            <i className="ti ti-receipt" style={{ fontSize:16 }} aria-hidden="true"></i>Packing Slips
          </button>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', padding:'16px 16px 4px', letterSpacing:'0.08em', textTransform:'uppercase' }}>Coming soon</div>
          <button style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 16px', background:'transparent', border:'none', cursor:'not-allowed', fontSize:13, color:'rgba(255,255,255,0.3)', textAlign:'left' }}>
            <i className="ti ti-chart-bar" style={{ fontSize:16 }} aria-hidden="true"></i>Reports
          </button>
        </nav>
        <div style={{ padding:'12px 16px', borderTop:'0.5px solid rgba(255,255,255,0.12)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:500, color:'#fff', flexShrink:0 }}>
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.85)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.name}</div>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.45)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.email}</div>
            </div>
          </div>
          <button onClick={handleLogout} style={{ width:'100%', padding:'6px 10px', borderRadius:6, border:'0.5px solid rgba(255,255,255,0.2)', background:'transparent', color:'rgba(255,255,255,0.65)', fontSize:12, cursor:'pointer', textAlign:'left' }}>
            <i className="ti ti-logout" style={{ fontSize:14, marginRight:6 }} aria-hidden="true"></i>{adminMode ? 'Back to Admin' : 'Sign out'}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflow:'auto' }}>

        {/* ── Packing Slips View ── */}
        {activeView === 'slips' && (
          <div style={{ padding:24 }}>
            <div style={{ fontSize:18, fontWeight:600, color:'var(--color-text-primary)', marginBottom:4 }}>Packing Slips</div>
            <div style={{ fontSize:13, color:'var(--color-text-secondary)', marginBottom:20 }}>{adminMode ? 'All packing slips' : 'Your packing slips'} — reprint or discard to reassign items.</div>
            {slipsLoading ? (
              <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>
            ) : slips.length === 0 ? (
              <div style={{ padding:48, textAlign:'center', color:'var(--color-text-secondary)', fontSize:14 }}>No packing slips yet</div>
            ) : (
              <div className="card" style={{ padding:0, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'var(--color-background-secondary)', borderBottom:'0.5px solid var(--color-border-tertiary)' }}>
                      <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Slip ID</th>
                      <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Created</th>
                      <th style={{ padding:'10px 14px', textAlign:'center', fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Items</th>
                      <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Status</th>
                      <th style={{ padding:'10px 14px', textAlign:'right', fontSize:11, fontWeight:600, color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slips.map(slip => (
                      <tr key={slip.slipId} style={{ borderBottom:'0.5px solid var(--color-border-tertiary)' }}>
                        <td style={{ padding:'10px 14px', fontFamily:'monospace', fontWeight:600, fontSize:13, color:'var(--color-text-primary)' }}>{slip.slipId}</td>
                        <td style={{ padding:'10px 14px', fontSize:13, color:'var(--color-text-secondary)' }}>{new Date(slip.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding:'10px 14px', fontSize:13, textAlign:'center', color:'var(--color-text-primary)' }}>{slip.itemCount}</td>
                        <td style={{ padding:'10px 14px' }}>
                          <span style={{ fontSize:11, fontWeight:600, color:'#fff', background: slip.status === 'printed' ? '#2E3A59' : '#999', padding:'2px 8px', borderRadius:10 }}>
                            {slip.status}
                          </span>
                        </td>
                        <td style={{ padding:'10px 14px', textAlign:'right', display:'flex', gap:8, justifyContent:'flex-end' }}>
                          {slip.status === 'printed' && (
                            <>
                              <button className="btn btn-sm" style={{ background:'#2E3A59', color:'#fff', border:'none' }}
                                onClick={() => printPackingSlip(slip.items || [], slip.slipId, slip.createdAt)}>
                                <i className="ti ti-printer" style={{ marginRight:4 }} aria-hidden="true"></i>Reprint
                              </button>
                              <button className="btn btn-sm btn-danger"
                                onClick={async () => {
                                  if (!window.confirm(`Discard packing slip ${slip.slipId}? Items will be released.`)) return;
                                  try {
                                    await discardPSPPackingSlip(slip.slipId);
                                    toast(`Packing slip ${slip.slipId} discarded`, 'success');
                                    loadSlips();
                                  } catch (err) {
                                    toast(err.response?.data?.error || 'Failed to discard', 'error');
                                  }
                                }}>
                                Discard Slip
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Queue View ── */}
        {activeView === 'queue' && <>

        {/* Top bar */}
        <div style={{ background:'var(--color-background-primary)', borderBottom:'0.5px solid var(--color-border-tertiary)', padding:'12px 20px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          {/* Group by checkboxes */}
          <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
            <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>Group by:</span>
            {GROUP_OPTIONS.map(opt => (
              <label key={opt.id} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', fontSize:13, color:'var(--color-text-primary)' }}>
                <input type="checkbox" checked={groupBy.includes(opt.id)} onChange={() => toggleGroup(opt.id)}
                  style={{ width:14, height:14, cursor:'pointer', accentColor:'#2E3A59' }} />
                {opt.label}
              </label>
            ))}
          </div>

          <div style={{ width:'0.5px', height:24, background:'var(--color-border-tertiary)', flexShrink:0 }}></div>

          {/* Search + filter */}
          <input className="input" style={{ width:240, fontSize:13 }} placeholder="Search company, order, SKU, product…"
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadItems()} />
          <StatusMultiSelect
            options={PSP_ITEM_STATUSES.map(s => ({ value: s, label: STATUS_LABELS[s] }))}
            selected={statusFilter} onChange={setStatusFilter}
            placeholder="All statuses" width={170} />
          {adminMode && (
            <select className="input" style={{ width:170, fontSize:13 }} value={pspFilter} onChange={e => setPspFilter(e.target.value)}>
              <option value="">All PSPs</option>
              {pspCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select className="input" style={{ width:170, fontSize:13 }} value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
            <option value="">All companies</option>
            {companies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="input" style={{ width:150, fontSize:13 }} value={sortDir} onChange={e => setSortDir(e.target.value)}>
            <option value="asc">Oldest first</option>
            <option value="desc">Newest first</option>
          </select>
          <div style={{ display:'flex', gap:4, flexShrink:0 }}>
            {[{ id:'', label:'All' }, { id:'today', label:'Today' }, { id:'week', label:'This week' }, { id:'month', label:'This month' }].map(p => (
              <button key={p.id} onClick={() => { setDatePreset(p.id); setDateFrom(''); setDateTo(''); }}
                className="btn btn-sm"
                style={{ fontSize:11, background: datePreset === p.id && !dateFrom && !dateTo ? '#2E3A59' : '', color: datePreset === p.id && !dateFrom && !dateTo ? '#fff' : '' }}>
                {p.label}
              </button>
            ))}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
            <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>From</span>
            <input type="date" className="input" style={{ fontSize:12, padding:'4px 8px', width:130 }}
              value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset(''); }} />
            <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>To</span>
            <input type="date" className="input" style={{ fontSize:12, padding:'4px 8px', width:130 }}
              value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePreset(''); }} />
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--color-text-primary)', cursor:'pointer', flexShrink:0 }}>
            <input type="checkbox" checked={hideSTS} onChange={e => setHideSTS(e.target.checked)} style={{ width:14, height:14, accentColor:'#2E3A59' }} />
            Hide shipped
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--color-text-primary)', cursor:'pointer', flexShrink:0 }}>
            <input type="checkbox" checked={rushOnly} onChange={e => setRushOnly(e.target.checked)} style={{ width:14, height:14, accentColor:'#2E3A59' }} />
            🔥 Rush only
          </label>
          <button className="btn btn-primary btn-sm" onClick={loadItems}>Search</button>
          <div style={{ marginLeft:'auto', fontSize:12, color:'var(--color-text-secondary)' }}>
            {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
            {orderedGroupBy.length > 0 && ` · grouped by ${orderedGroupBy.join(' → ')}`}
          </div>
        </div>

        {/* Bulk action bar */}
        {someSelected && (
          <div style={{ background:'#EFF6FF', borderBottom:'0.5px solid var(--color-border-tertiary)', padding:'10px 20px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            {bulkConfirm && <ConfirmPopup message={bulkConfirm.message} onConfirm={bulkConfirm.onConfirm} onCancel={() => setBulkConfirm(null)} />}
            <span style={{ fontSize:13, fontWeight:500, color:'#1E40AF' }}>
              {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
            </span>
            {commonStatus ? (
              <>
                <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>
                  All currently {STATUS_LABELS[commonStatus]} — move to:
                </span>
                <select className="input" style={{ width:180, fontSize:13 }} value={bulkTarget} onChange={e => setBulkTarget(e.target.value)}>
                  <option value="">Select status…</option>
                  {bulkTargetOptions.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" disabled={!bulkTarget} onClick={handleBulkApply}>Apply</button>
              </>
            ) : (
              <span style={{ fontSize:12, color:'#B45309' }}>
                Selected items have different statuses — please select items with the same status to change them together.
              </span>
            )}
            <button className="btn btn-sm" style={{ marginLeft:'auto' }} onClick={() => setSelectedIds([])}>Clear selection</button>
            {packingSelected.length > 0 && (() => {
              // Determine if this is a reprint (all selected in-production items share same slip ID)
              const slipIds = [...new Set(packingSelected.map(i => i.packingSlipId).filter(Boolean))];
              const allOnSameSlip = slipIds.length === 1 && packingSelected.every(i => i.packingSlipId === slipIds[0]);
              const slipItemsInList = lineItems.filter(i => i.packingSlipId === slipIds[0] && i.itemStatus === 'packing');
              const isReprint = allOnSameSlip && packingSelected.length === slipItemsInList.length;
              const isClean = packingSelected.every(i => !i.packingSlipId);

              if (isReprint) return (
                <>
                  <button className="btn btn-sm" style={{ background:'#2E3A59', color:'#fff', border:'none' }}
                    onClick={() => {
                      printPackingSlip(packingSelected, slipIds[0]);
                      toast(`Reprinting packing slip ${slipIds[0]}`, 'success');
                    }}>
                    <i className="ti ti-printer" style={{ marginRight:4 }} aria-hidden="true"></i>
                    Reprint {slipIds[0]} ({packingSelected.length} item{packingSelected.length !== 1 ? 's' : ''})
                  </button>
                  <button className="btn btn-sm btn-danger"
                    onClick={async () => {
                      if (!window.confirm(`Discard packing slip ${slipIds[0]}? Items will be released and can be added to a new slip.`)) return;
                      try {
                        await discardPSPPackingSlip(slipIds[0]);
                        toast(`Packing slip ${slipIds[0]} discarded`, 'success');
                        loadItems();
                      } catch (err) {
                        toast(err.response?.data?.error || 'Failed to discard', 'error');
                      }
                    }}>
                    Discard Slip
                  </button>
                </>
              );

              if (isClean) return (
                <button className="btn btn-sm" style={{ background:'#2E3A59', color:'#fff', border:'none' }}
                  onClick={async () => {
                    try {
                      const { data } = await createPackingSlip(packingSelected.map(i => i.id));
                      printPackingSlip(packingSelected, data.slipId);
                      toast(`Packing slip ${data.slipId} created`, 'success');
                      loadItems();
                    } catch (err) {
                      toast(err.response?.data?.error || 'Failed to create packing slip', 'error');
                    }
                  }}>
                  <i className="ti ti-printer" style={{ marginRight:4 }} aria-hidden="true"></i>
                  Print Packing Slip ({packingSelected.length} item{packingSelected.length !== 1 ? 's' : ''})
                </button>
              );

              // Mixed state — show discard for all tagged slips
              if (slipIds.length > 0) return (
                <button className="btn btn-sm btn-danger"
                  onClick={async () => {
                    const slipList = slipIds.join(', ');
                    if (!window.confirm(`Discard packing slip${slipIds.length > 1 ? 's' : ''} ${slipList}? Items will be released and can be added to a new slip.`)) return;
                    try {
                      await Promise.all(slipIds.map(id => discardPSPPackingSlip(id)));
                      toast(`Packing slip${slipIds.length > 1 ? 's' : ''} ${slipList} discarded`, 'success');
                      loadItems();
                    } catch (err) {
                      toast(err.response?.data?.error || 'Failed to discard', 'error');
                    }
                  }}>
                  Discard Slip{slipIds.length > 1 ? 's' : ''}
                </button>
              );

              return null;
            })()}
          </div>
        )}

        {/* Content */}
        <div style={{ flex:1 }}>
          {loading && lineItems.length === 0 ? (
            <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>
          ) : lineItems.length === 0 ? (
            <div style={{ padding:48, textAlign:'center', color:'var(--color-text-secondary)', fontSize:14 }}>No items in queue</div>
          ) : (
            <div>
              <TableHeader allSelected={allSelected} someSelected={someSelected} onToggleAll={toggleSelectAll} selectableCount={selectableItems.length} />
              <GroupedList items={lineItems} groupLevels={orderedGroupBy} depth={0} toast={toast} onRefresh={loadItems} openDownloadModal={openDownloadModal}
                selectedIds={selectedIds} onToggleSelect={toggleSelect} onOptimisticUpdate={optimisticUpdate} />
            </div>
          )}
        </div>
        </>}
      </div>
    </div>
    </PSPAdminContext.Provider>
  );
}
